import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import SwapVertRounded from '@mui/icons-material/SwapVertRounded';
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  Divider,
  Grow,
  Link,
  Paper,
  Popper as MUIPopper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useRef, useState } from 'react';
import type { ColumnSelectOption } from '@/shared/ui';
import { ColumnSelect } from '@/shared/ui';
import type { SplitSort } from '../../model/types';
import { SplitSortBy, SplitSortDirection } from '../../model/types';

const Popper = styled(MUIPopper)(({ theme }) => ({
  zIndex: theme.zIndex.modal,
}));

interface Props {
  sort: SplitSort;
  defaultSort?: SplitSort;
  metricOptions: ColumnSelectOption[];
  resolvedMetric: string;
  isChronologicalSplit: boolean;
  onChange: (patch: Partial<SplitSort>) => void;
  onReset: () => void;
}

const getOrderLabels = (by: SplitSortBy, isChronological: boolean) => {
  if (by === SplitSortBy.value) {
    return {
      [SplitSortDirection.desc]: 'High → low',
      [SplitSortDirection.asc]: 'Low → high',
    };
  }

  if (isChronological) {
    return {
      [SplitSortDirection.desc]: 'Newest first',
      [SplitSortDirection.asc]: 'Oldest first',
    };
  }

  return {
    [SplitSortDirection.desc]: 'Z → A',
    [SplitSortDirection.asc]: 'A → Z',
  };
};

export function SplitSortControl({
  sort,
  defaultSort,
  metricOptions,
  resolvedMetric,
  isChronologicalSplit,
  onChange,
  onReset,
}: Props) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const orderLabels = getOrderLabels(sort.by, isChronologicalSplit);
  const nameOf = (metric: string) =>
    metricOptions.find((option) => option.value === metric)?.name ?? metric;
  const keyLabel =
    sort.by === SplitSortBy.name ? 'Name' : nameOf(resolvedMetric);

  const describe = (value: SplitSort) => {
    const labels = getOrderLabels(value.by, isChronologicalSplit);
    const key =
      value.by === SplitSortBy.name
        ? 'Name'
        : nameOf(value.metric || resolvedMetric);

    return `${key}, ${labels[value.direction].toLowerCase()}`;
  };

  const flipDirection = () => {
    onChange({
      direction:
        sort.direction === SplitSortDirection.desc
          ? SplitSortDirection.asc
          : SplitSortDirection.desc,
    });
  };

  return (
    <>
      <Box
        ref={anchorRef}
        sx={{
          height: 44,
          display: 'flex',
          flexShrink: 0,
          border: '1px solid',
          borderColor: open ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
          borderRadius: 2,
          overflow: 'hidden',
          color: open ? 'primary.main' : '#343844',
        }}
      >
        <ButtonBase
          aria-label={`Sort by ${keyLabel}, open sort options`}
          aria-haspopup="dialog"
          aria-expanded={open}
          sx={{
            px: 1.5,
            gap: 0.5,
            backgroundColor: open ? '#EBEFFF' : 'transparent',
          }}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
        >
          <SwapVertRounded sx={{ fontSize: 18 }} />
          <Typography variant="body2" noWrap>
            {keyLabel}
          </Typography>
        </ButtonBase>
        <Divider orientation="vertical" flexItem />
        <Tooltip arrow title={orderLabels[sort.direction]}>
          <ButtonBase
            aria-label="Reverse sort direction"
            sx={{ px: 1 }}
            onClick={flipDirection}
          >
            {sort.direction === SplitSortDirection.desc ? (
              <ArrowDownwardRounded sx={{ fontSize: 18 }} />
            ) : (
              <ArrowUpwardRounded sx={{ fontSize: 18 }} />
            )}
          </ButtonBase>
        </Tooltip>
      </Box>
      {open && (
        <Popper
          open
          anchorEl={anchorRef.current}
          placement="bottom-end"
          transition
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
              <Paper sx={{ mt: 0.5, width: 300 }} elevation={3}>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Sort by
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        fullWidth
                        size="small"
                        value={sort.by}
                        onChange={(_, value: SplitSortBy | null) => {
                          if (value) {
                            onChange({ by: value });
                          }
                        }}
                      >
                        <ToggleButton value={SplitSortBy.name}>
                          Name
                        </ToggleButton>
                        <ToggleButton value={SplitSortBy.value}>
                          Value
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {sort.by === SplitSortBy.value && (
                      <ColumnSelect
                        label="Metric"
                        options={metricOptions}
                        value={resolvedMetric}
                        onChange={(metric) => onChange({ metric })}
                      />
                    )}

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Order
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          p: 0.5,
                          borderRadius: 1,
                          backgroundColor: 'action.hover',
                        }}
                      >
                        {[
                          SplitSortDirection.desc,
                          SplitSortDirection.asc,
                        ].map((direction) => (
                          <Tooltip
                            arrow
                            key={direction}
                            title={orderLabels[direction]}
                          >
                            <ButtonBase
                              aria-label={orderLabels[direction]}
                              aria-pressed={sort.direction === direction}
                              sx={{
                                flex: 1,
                                py: 0.5,
                                gap: 0.5,
                                borderRadius: 1,
                                backgroundColor:
                                  sort.direction === direction
                                    ? '#FFFFFF'
                                    : 'transparent',
                                boxShadow:
                                  sort.direction === direction ? 1 : 0,
                              }}
                              onClick={() => onChange({ direction })}
                            >
                              {direction === SplitSortDirection.desc ? (
                                <ArrowDownwardRounded sx={{ fontSize: 16 }} />
                              ) : (
                                <ArrowUpwardRounded sx={{ fontSize: 16 }} />
                              )}
                              <Typography variant="caption">
                                {orderLabels[direction]}
                              </Typography>
                            </ButtonBase>
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>

                    {defaultSort && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Default for this split: {describe(defaultSort)}
                        </Typography>
                        <Box>
                          <Link
                            component="button"
                            type="button"
                            variant="caption"
                            onClick={onReset}
                          >
                            Reset to default
                          </Link>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </>
  );
}
